import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetch_availability,
  fetch_day_view,
  create_rule,
  update_rule,
  delete_rule,
  create_exception,
  delete_exception,
  update_config,
} from './api';
import type {
  CreateRuleRequest,
  UpdateRuleRequest,
  CreateExceptionRequest,
  UpdateConfigRequest,
} from './types';

const AVAILABILITY_KEY = ['vendor-availability'] as const;

export function use_availability() {
  return useQuery({ queryKey: AVAILABILITY_KEY, queryFn: fetch_availability });
}

export function use_day_view(service_id: string | undefined, date: string | undefined) {
  return useQuery({
    queryKey: ['availability-day', service_id, date],
    queryFn: () => fetch_day_view(service_id as string, date as string),
    enabled: !!service_id && !!date,
  });
}

function invalidate_all(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: AVAILABILITY_KEY });
  qc.invalidateQueries({ queryKey: ['availability-day'] });
}

export function use_create_rule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRuleRequest) => create_rule(input),
    onSuccess: () => invalidate_all(qc),
  });
}

export function use_update_rule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateRuleRequest }) => update_rule(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: AVAILABILITY_KEY }),
  });
}

export function use_delete_rule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => delete_rule(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: AVAILABILITY_KEY }),
  });
}

export function use_create_exception() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateExceptionRequest) => create_exception(input),
    onSuccess: () => invalidate_all(qc),
  });
}

export function use_delete_exception() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => delete_exception(id),
    onSuccess: () => invalidate_all(qc),
  });
}

export function use_update_config() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateConfigRequest) => update_config(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: AVAILABILITY_KEY }),
  });
}
